import { ProgrammeApproval } from '@/components/ProgrammeApproval';
import { ActiveProgrammeStart } from '@/components/ProgrammeStartDate';
import { TrainingReview } from '@/components/TrainingReview';
import { NutritionReview } from '@/components/NutritionReview';
import { GOALS } from '@/lib/goals';
import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { api, privateFileUrl } from '@/lib/api';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Trash2, Send, Sparkles, ClipboardList, StickyNote } from 'lucide-react';
import { format } from 'date-fns';
import { RegenScoreRing } from '@/components/RegenScoreRing';
import { CoachDashboard } from './CoachDashboard';
import { WeeklyDigest } from '@/components/WeeklyDigest';

const STATUS_OPTIONS = [
  { value: 'onboarding_pending', label: 'Onboarding Pending', color: '#7E8C9E' },
  { value: 'program_preparation', label: 'Program Preparation', color: '#FFB800' },
  { value: 'active', label: 'Active', color: '#65A30D' },
  { value: 'paused', label: 'Paused', color: '#FF8A4C' },
  { value: 'completed', label: 'Completed', color: '#00C8FF' },
];
const statusMeta = (s) => STATUS_OPTIONS.find(o => o.value === s) || STATUS_OPTIONS[0];

export default function ClientDetail() {
  const { id } = useParams();
  const [search] = useSearchParams();
  const nav = useNavigate();
  const [client, setClient] = useState(null);
  const [workouts, setWorkouts] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [nutritionPrograms, setNutritionPrograms] = useState([]);
  const [recoveryPrograms, setRecoveryPrograms] = useState([]);
  const [myPrograms, setMyPrograms] = useState([]);
  const [mealPlans, setMealPlans] = useState([]);
  const [recoveries, setRecoveries] = useState([]);
  const [assignments, setAssignments] = useState({ w: [], m: [], r: [] });
  const [msgBody, setMsgBody] = useState('');
  const [thread, setThread] = useState([]);
  const [onboarding, setOnboarding] = useState(null);
  const [notes, setNotes] = useState([]);

  const load = async () => {
    const c = await api.get(`/clients/${id}`).catch(() => null);
    if (!c) { toast.error('Client not found'); nav('/admin/clients'); return; }
    setClient(c.data);
    setOnboarding(c.data.onboarding_data || null);
    const w = await api.get('/workouts'); setWorkouts(w.data);
    const tp = await api.get('/training-programs').catch(() => ({ data: [] })); setPrograms(tp.data);
    const np = await api.get('/nutrition-programs').catch(() => ({ data: [] })); setNutritionPrograms(np.data);
    const rp = await api.get('/recovery-programs').catch(() => ({ data: [] })); setRecoveryPrograms(rp.data);
    const mp2 = await api.get('/my-programs', { params: { client_id: id } }).catch(() => ({ data: [] })); setMyPrograms(mp2.data);
    const m = await api.get('/meal-plans'); setMealPlans(m.data);
    const r = await api.get('/recovery/activities'); setRecoveries(r.data);
    const wa = await api.get(`/workouts/assignments/admin/${id}`); 
    const ma = await api.get('/meal-plans/assignments', { params: { client_id: id } }).catch(() => ({ data: [] }));
    const ra = await api.get('/recovery/assignments', { params: { client_id: id } }).catch(() => ({ data: [] }));
    setAssignments({ w: wa.data, m: ma.data.filter(x => x.client_id === id), r: ra.data.filter(x => x.client_id === id) });
    const t = await api.get(`/messages/${id}`); setThread(t.data);
    const n = await api.get(`/clients/${id}/coach-notes`).catch(() => ({ data: [] })); setNotes(n.data);
  };
  useEffect(() => { load(); }, [id]);

  if (!client) return <div className="text-muted-foreground">Loading…</div>;

  const assign = async (type, item_id, date) => {
    try {
      if (type === 'workout') await api.post('/workouts/assign', { workout_id: item_id, client_id: id, date });
      if (type === 'meal') await api.post('/meal-plans/assign', { meal_plan_id: item_id, client_id: id, date });
      if (type === 'recovery') await api.post('/recovery/assign', { activity_id: item_id, client_id: id, date });
      toast.success('Assigned');
      load();
    } catch { toast.error('Failed'); }
  };

  const togglePause = async () => {
    try {
      if (client.status === 'active') await api.put(`/clients/${id}/pause`);
      else await api.put(`/clients/${id}/activate`);
      toast.success('Updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update the client'); }
    load();
  };

  const changeStatus = async (status) => {
    await api.put(`/clients/${id}/status`, { status });
    toast.success(status === 'active' ? 'Client activated — program unlocked & notified' : 'Status updated');
    load();
  };

  const changeGoalType = async (goal_type) => {
    await api.put(`/clients/${id}/goal-type`, { goal_type });
    toast.success('Goal type updated — dashboard will adapt');
    load();
  };

  const activateClient = async () => {
    try {
      await api.put(`/clients/${id}/activate`);
      toast.success('Client activated — programme released & client notified');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not activate the client'); }
    load();
  };

  const deleteClient = async () => {
    if (!confirm('Delete this client and all their data?')) return;
    await api.delete(`/clients/${id}`);
    toast.success('Client deleted');
    nav('/admin/clients');
  };

  const sendMsg = async () => {
    if (!msgBody.trim()) return;
    await api.post('/messages', { to_user_id: id, body: msgBody });
    setMsgBody('');
    const t = await api.get(`/messages/${id}`); setThread(t.data);
  };

  return (
    <div className="space-y-6" data-testid="admin-client-detail">
      <button onClick={() => nav('/admin/clients')} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="w-4 h-4" /> All clients</button>

      <div className="regen-card-elevated p-6 flex items-start gap-6">
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#00C8FF] to-[var(--c-green)] flex items-center justify-center text-[#081726] font-bold text-xl">{(client.name || '?').split(' ').map(p => p[0]).slice(0,2).join('')}</div>
        <div className="flex-1">
          <h1 className="font-display font-black text-2xl">{client.name}</h1>
          <div className="text-sm text-muted-foreground">{[client.email, client.country, client.program || ({ regen_plus: 'YourReGen+', regen_method: 'YourReGen Protocol' })[client.subscription_tier] || 'No plan'].filter(Boolean).join(' · ')}</div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <span className="pill text-xs font-semibold" data-testid="client-status-badge"
              style={{ background: `${statusMeta(client.status).color}1a`, color: statusMeta(client.status).color }}>
              {statusMeta(client.status).label}
            </span>
          </div>
          <div className="mt-3 flex gap-2 flex-wrap items-center">
            {client.status !== 'active' && !client.pending_block && (
              <Button onClick={activateClient} size="sm" data-testid="activate-client-btn"
                className="bg-[var(--c-green)] text-[#081726] font-semibold hover:bg-[var(--c-green-soft)]">
                <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Activate Client
              </Button>
            )}
            <Select value={client.status} onValueChange={changeStatus}>
              <SelectTrigger className="w-[190px] h-9 bg-regen-card border-[var(--c-hair)] text-sm" data-testid="status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={client.goal_type || client.onboarding_data?.primary_goal || ''} onValueChange={changeGoalType}>
              <SelectTrigger className="w-[170px] h-9 bg-regen-card border-[var(--c-hair)] text-sm" data-testid="goal-type-select"><SelectValue placeholder="Goal" /></SelectTrigger>
              <SelectContent>
                {GOALS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={deleteClient} variant="outline" size="sm" data-testid="delete-btn" className="border-red-500/30 text-red-400 hover:bg-red-500/10">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
            </Button>
          </div>
        </div>
        <div className="flex flex-col items-center gap-2 shrink-0">
          <div className="label-mini">YourReGen Score</div>
          <RegenScoreRing score={client.regen_score?.score ?? 0} level={`Score: ${client.regen_score?.level ?? '—'}`} size={110} />
          <div className="pill text-xs font-semibold bg-[#081726] text-white border border-[#FFB800]" data-testid="client-training-level-badge">
            Training level: {client.training_level || '—'}
          </div>
        </div>
      </div>

      <ProgrammeApproval client={client} onChanged={load} />
      {!client.pending_block && (() => {
        const tp = myPrograms.find(p => p.program_type === 'training') || myPrograms.find(p => p.program_type === 'nutrition');
        return tp ? <ActiveProgrammeStart key={tp.start_date} clientId={id} start={tp.start_date} onChanged={load} /> : null;
      })()}

      <Tabs defaultValue={search.get('tab') || 'overview'}>
        <TabsList className="bg-regen-card border border-[var(--c-hair)] w-full overflow-x-auto flex-nowrap justify-start no-scrollbar">
          <TabsTrigger value="overview" data-testid="cd-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="onboarding" data-testid="cd-tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="training" data-testid="cd-tab-training">Training</TabsTrigger>
          <TabsTrigger value="nutrition" data-testid="cd-tab-nutrition">Nutrition</TabsTrigger>
          <TabsTrigger value="recovery" data-testid="cd-tab-recovery">Recovery</TabsTrigger>
          <TabsTrigger value="messages" data-testid="cd-tab-messages">Messages</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4 space-y-4">
          <AccessPanel client={client} reload={load} />
          <WeeklyDigest clientId={id} />
          <CoachDashboard clientId={id} />
        </TabsContent>

        <TabsContent value="onboarding" className="mt-4 space-y-4">
          <OnboardingView data={onboarding} />
          <CoachNotes clientId={id} notes={notes} reload={load} />
        </TabsContent>

        <TabsContent value="training" className="mt-4 space-y-4">
          <TrainingReview clientId={id} onChanged={load} version={`${client.status}|${client.pending_block?.created_at || ''}|${client.pending_block?.approved || ''}`} />
          <ProgramAssign clientId={id} programs={programs} actives={myPrograms.filter(p => p.program_type === 'training')} reload={load} />
          <AssignSection title="Assign Single Workout" items={workouts} type="workout" onAssign={assign} testid="assign-workout" />
          <AssignmentList list={assignments.w} title="Assigned Workouts" labelKey="workout_snapshot.name" />
        </TabsContent>
        <TabsContent value="nutrition" className="mt-4 space-y-4">
          <NutritionReview clientId={id} version={`${client.status}|${client.pending_block?.created_at || ''}|${client.pending_block?.approved || ''}`} />
          <ProgramAssign clientId={id} type="nutrition" programs={nutritionPrograms} actives={myPrograms.filter(p => p.program_type === 'nutrition')} reload={load} />
          <NutritionTargetEditor client={client} onSaved={load} />
          <DailyNutritionReview clientId={id} />
          <ClientCustomFoods clientId={id} />
          <AssignSection title="Assign Single Meal Plan" items={mealPlans} type="meal" onAssign={assign} testid="assign-meal" />
          <AssignmentList list={assignments.m} title="Assigned Meal Plans" labelKey="plan_snapshot.name" />
        </TabsContent>
        <TabsContent value="recovery" className="mt-4 space-y-4">
          <ProgramAssign clientId={id} type="recovery" programs={recoveryPrograms} actives={myPrograms.filter(p => p.program_type === 'recovery')} reload={load} />
          <AssignSection title="Assign Single Recovery" items={recoveries} type="recovery" onAssign={assign} testid="assign-recovery" />
          <AssignmentList list={assignments.r} title="Assigned Recovery" labelKey="activity_snapshot.name" />
        </TabsContent>
        <TabsContent value="messages" className="mt-4">
          <div className="regen-card-elevated p-5">
            <div className="space-y-3 max-h-96 overflow-y-auto mb-4">
              {thread.map(m => (
                <div key={m.id} className={`flex ${m.from_user_id === id ? 'justify-start' : 'justify-end'}`}>
                  <div className={`max-w-[70%] p-3 rounded-2xl text-sm ${m.from_user_id === id ? 'bg-[var(--c-film)]' : 'bg-[#00C8FF] text-[#081726]'}`}>
                    {m.body}
                  </div>
                </div>
              ))}
              {thread.length === 0 && <div className="text-center text-muted-foreground text-sm py-8">No messages yet.</div>}
            </div>
            <div className="flex gap-2">
              <Input value={msgBody} onChange={e => setMsgBody(e.target.value)} placeholder="Type a message…" data-testid="msg-input" className="bg-regen-card border-[var(--c-hair)]" />
              <Button onClick={sendMsg} data-testid="msg-send" className="bg-[#00C8FF] text-[#081726]"><Send className="w-4 h-4" /></Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ProgramAssign({ clientId, programs, actives = [], reload, type = 'training' }) {
  const META = {
    training: { color: '#00C8FF', label: 'Training Programs', endpoint: '/training-programs/assign', countKey: 'workouts_created', unit: 'workouts' },
    nutrition: { color: '#65A30D', label: 'Nutrition Programs', endpoint: '/nutrition-programs/assign', countKey: 'days_created', unit: 'days' },
    recovery: { color: '#A78BFA', label: 'Recovery Programs', endpoint: '/recovery-programs/assign', countKey: 'activities_created', unit: 'activities' },
  }[type];
  const [sel, setSel] = useState('');
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const assign = async () => {
    if (!sel) return;
    try {
      const r = await api.post(META.endpoint, { program_id: sel, client_id: clientId, start_date: date });
      toast.success(`Program assigned — ${r.data[META.countKey]} ${META.unit} scheduled`);
      setSel(''); reload();
    } catch { toast.error('Failed to assign'); }
  };
  const removeAssignment = async (paId) => {
    if (!confirm('Remove this assigned program? Future pending items will be cleared (completed history kept).')) return;
    try { await api.delete(`/program-assignments/${paId}`); toast.success('Program removed'); reload(); }
    catch { toast.error('Failed to remove'); }
  };
  return (
    <div className="regen-card-elevated p-5" data-testid={`program-assign-${type}`}>
      <div className="label-mini mb-3" style={{ color: META.color }}>{META.label}</div>
      {actives.map((active) => (
        <div key={active.id} className="rounded-xl p-4 mb-3" style={{ background: `${META.color}1a`, border: `1px solid ${META.color}33` }} data-testid={`active-${type}-program`}>
          <div className="flex items-center justify-between">
            <div>
              <div className="font-display font-bold">{active.name}</div>
              <div className="text-xs text-muted-foreground mt-0.5">Week {active.progress?.current_week} of {active.duration_weeks} · Started {active.start_date}</div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="font-mono text-2xl" style={{ color: META.color }}>{active.progress?.completion ?? 0}%</div>
                <div className="text-[10px] text-muted-foreground">{active.progress?.done}/{active.progress?.total} done</div>
              </div>
              <button onClick={() => removeAssignment(active.id)} data-testid={`remove-${type}-program-${active.id}`} className="text-red-400 hover:text-red-300 p-2" title="Remove program"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
      ))}
      <div className="flex gap-2">
        <Select value={sel} onValueChange={setSel}>
          <SelectTrigger data-testid={`program-assign-${type}-select`} className="bg-regen-card border-[var(--c-hair)] flex-1"><SelectValue placeholder="Choose a program to add…" /></SelectTrigger>
          <SelectContent>{programs.map(p => <SelectItem key={p.id} value={p.id}>{p.name} · {p.weeks?.length || 0}wks</SelectItem>)}</SelectContent>
        </Select>
        <Input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid={`program-assign-${type}-date`} className="bg-regen-card border-[var(--c-hair)] w-44" />
        <Button onClick={assign} data-testid={`program-assign-${type}-btn`} className="font-semibold" style={{ background: META.color, color: '#081726' }}>Assign Program</Button>
      </div>
      <p className="text-[11px] text-muted-foreground mt-2">Days map to real weekdays from the start date (a "Wednesday" workout lands on Wednesday). You can assign multiple programs and remove any of them above.</p>
    </div>
  );
}

function AssignSection({ title, items, type, onAssign, testid }) {
  const [sel, setSel] = useState('');
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  return (
    <div className="regen-card-elevated p-5 mb-4">
      <div className="label-mini mb-3">{title}</div>
      <div className="flex gap-2">
        <Select value={sel} onValueChange={setSel}>
          <SelectTrigger data-testid={`${testid}-select`} className="bg-regen-card border-[var(--c-hair)] flex-1"><SelectValue placeholder="Choose…" /></SelectTrigger>
          <SelectContent>{items.map(it => <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>)}</SelectContent>
        </Select>
        <Input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid={`${testid}-date`} className="bg-regen-card border-[var(--c-hair)] w-48" />
        <Button onClick={() => sel && onAssign(type, sel, date)} data-testid={`${testid}-btn`} className="bg-[#00C8FF] text-[#081726] font-semibold">Assign</Button>
      </div>
    </div>
  );
}

function AssignmentList({ list, title, labelKey }) {
  const get = (o, path) => path.split('.').reduce((a, k) => a?.[k], o);
  const [open, setOpen] = useState(null);
  return (
    <div className="regen-card-elevated p-5">
      <div className="label-mini mb-3">{title}</div>
      <div className="space-y-2 max-h-80 overflow-y-auto">
        {list.length === 0 && <div className="text-sm text-muted-foreground py-4 text-center">None yet</div>}
        {list.map(a => (
          <div key={a.id} className="py-2 border-b border-[var(--c-hair)] last:border-0">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium">{get(a, labelKey) || '—'}{a.extra && <span className="ml-2 pill bg-[#00C8FF]/15 text-[#00C8FF]" data-testid={`admin-extra-tag-${a.id}`}>{a.custom_built ? 'Extra · built by client' : 'Extra · added by client'}</span>}
                {a.custom_snapshot && <button onClick={() => setOpen(open === a.id ? null : a.id)} className="ml-2 pill bg-[var(--c-green-soft)] text-[var(--c-green-ink)]" data-testid={`admin-custom-tag-${a.id}`}>Customised by client</button>}</div>
              <div className="text-xs text-muted-foreground font-mono">{a.date}</div>
            </div>
            <span className={`pill ${a.status === 'completed' || a.followed ? 'bg-[var(--c-green-soft)] text-[var(--c-green-ink)]' : 'bg-[var(--c-film)] text-muted-foreground'}`}>
              {a.status === 'completed' || a.followed ? 'Done' : 'Pending'}
            </span>
          </div>
          {open === a.id && <CustomCompare a={a} />}
          </div>
        ))}
      </div>
    </div>
  );
}

function CustomCompare({ a }) {
  const [ex, setEx] = useState({});
  useEffect(() => { api.get('/exercises').then(r => setEx(Object.fromEntries((r.data || []).map(e => [e.id, e.name])))).catch(() => {}); }, []);
  const line = (it) => `${ex[it.exercise_id] || it.exercise_name || 'Exercise'} — ${it.sets} × ${it.reps}${it.weight ? ` @ ${it.weight}` : ''} · ${it.rest_seconds}s`;
  const col = (title, items, notes) => (
    <div className="regen-card p-3 min-w-0">
      <div className="label-mini mb-1">{title}</div>
      {(items || []).map((it, i) => <div key={i} className="text-xs text-muted-foreground truncate">{line(it)}</div>)}
      {notes && <div className="text-xs text-[#00C8FF] mt-1 italic">{notes}</div>}
    </div>
  );
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2" data-testid={`admin-custom-compare-${a.id}`}>
      {col("Coach's original", a.workout_snapshot?.items)}
      {col("Client's version", a.custom_snapshot?.items, a.custom_snapshot?.client_notes)}
    </div>
  );
}

function ClientOverview({ client, workouts, meals, recovery }) {
  const start = client.starting_weight_kg ?? 0;
  const current = client.current_weight_kg ?? start;
  const goal = client.goal_weight_kg ?? start;
  const lost = Math.max(0, start - current).toFixed(1);
  const progress = (start && goal && current && start !== goal)
    ? Math.min(100, Math.round(((start - current) / (start - goal)) * 100))
    : 0;
  const wDone = workouts.filter(a => a.status === 'completed').length;
  const wTotal = workouts.length;
  const mDone = meals.filter(a => a.followed).length;
  const mTotal = meals.length;
  const rDone = recovery.filter(a => a.status === 'completed').length;
  const rTotal = recovery.length;
  const adherence = (wTotal + mTotal + rTotal) > 0
    ? Math.round(((wDone + mDone + rDone) / (wTotal + mTotal + rTotal)) * 100)
    : 0;

  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KCard label="Starting" value={`${start || '—'} kg`} color="#C9D3DF" />
        <KCard label="Current" value={`${current || '—'} kg`} color="#00C8FF" testid="ovw-current" />
        <KCard label="Goal" value={`${goal || '—'} kg`} color='#65A30D' testid="ovw-goal" />
        <KCard label="Lost" value={`-${lost} kg`} color="#FFB800" testid="ovw-lost" />
      </div>

      <div className="regen-card-elevated p-5">
        <div className="flex items-center justify-between mb-2">
          <div className="label-mini">Goal Progress</div>
          <div className="font-mono text-2xl text-[#00C8FF]" data-testid="ovw-progress">{progress}%</div>
        </div>
        <div className="h-2 bg-[var(--c-film)] rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#00C8FF] to-[var(--c-green)] shadow-[0_0_12px_rgba(0,200,255,0.5)]" style={{ width: `${progress}%` }} />
        </div>
        <div className="mt-3 text-xs text-muted-foreground">Started: {start} kg → Current: {current} kg → Goal: {goal} kg</div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="regen-card-elevated p-4">
          <div className="label-mini">Program Adherence</div>
          <div className="font-mono text-3xl mt-1 text-[var(--c-green-ink)]" data-testid="ovw-adherence">{adherence}%</div>
          <div className="mt-3 space-y-2 text-xs">
            <Row label="Training" done={wDone} total={wTotal} color="#00C8FF" />
            <Row label="Nutrition" done={mDone} total={mTotal} color="#FFB800" />
            <Row label="Recovery" done={rDone} total={rTotal} color="#7DD3FC" />
          </div>
        </div>

        <div className="regen-card-elevated p-4">
          <div className="label-mini">Recent Recovery Assignments</div>
          {recovery.length === 0
            ? <div className="text-xs text-muted-foreground py-3 text-center">None assigned yet</div>
            : <div className="mt-2 space-y-1.5 max-h-44 overflow-y-auto">
                {recovery.slice(0, 6).map(a => (
                  <div key={a.id} className="flex items-center justify-between text-xs py-1.5 border-b border-[var(--c-hair)] last:border-0">
                    <div className="min-w-0">
                      <div className="truncate font-medium">{a.activity_snapshot?.name}</div>
                      <div className="text-[10px] text-muted-foreground font-mono">{a.date} · {a.activity_snapshot?.metric_type === 'steps' ? `${(a.activity_snapshot?.target_value || 0).toLocaleString()} steps` : `${a.activity_snapshot?.target_value || a.activity_snapshot?.duration_min} min`}</div>
                    </div>
                    <span className={`pill text-[9px] ${a.status === 'completed' ? 'bg-[var(--c-green-soft)] text-[var(--c-green-ink)]' : 'bg-[var(--c-film)] text-muted-foreground'}`}>{a.status}</span>
                  </div>
                ))}
              </div>
          }
        </div>
      </div>
    </>
  );
}

function KCard({ label, value, color, testid }) {
  return (
    <div className="regen-card-elevated p-4" data-testid={testid}>
      <div className="label-mini">{label}</div>
      <div className="font-mono text-2xl mt-1" style={{ color }}>{value}</div>
    </div>
  );
}

function Row({ label, done, total, color }) {
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between mb-0.5">
        <span>{label}</span>
        <span className="font-mono text-muted-foreground">{done}/{total} · {pct}%</span>
      </div>
      <div className="h-1 bg-[var(--c-film)] rounded-full overflow-hidden">
        <div className="h-full" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}


const OB_LABELS = {
  full_name: 'Full Name', age: 'Age', gender: 'Gender', gender_other: 'Gender (specified)',
  height_cm: 'Height (cm)', current_weight_kg: 'Current Weight (kg)', country: 'Country', city: 'City', occupation: 'Occupation',
  primary_goal: 'Primary Goal', primary_goal_other: 'Goal (specified)', target_weight_kg: 'Target Weight (kg)',
  motivation: 'Motivation', success_definition: 'Definition of Success',
  medical_conditions: 'Medical Conditions', medical_conditions_detail: 'Medical Details', injuries: 'Injuries',
  injuries_detail: 'Injury Details', medications: 'Medications / Supplements', allergies: 'Allergies',
  avg_sleep_hours: 'Avg Sleep (hrs)', stress_level: 'Stress Level (1-10)', energy_level: 'Energy Level (1-10)',
  alcohol: 'Alcohol', smoking: 'Smoking',
  activity_level: 'Activity Level', training_experience: 'Training Experience', training_types: 'Current Training',
  training_types_other: 'Training (specified)', days_per_week: 'Days / Week', equipment_access: 'Equipment Access',
  equipment_access_other: 'Equipment (specified)', exercise_likes: 'Exercises Enjoyed', exercise_dislikes: 'Exercises Disliked',
  diet_type: 'Diet Type', diet_type_other: 'Diet (specified)', foods_avoid: 'Foods Avoided', meals_per_day: 'Meals / Day',
  water_intake: 'Water Intake', caffeine: 'Caffeine', cooking_ability: 'Cooking Ability', recovery_habits: 'Recovery Habits',
  nutrition_challenge: 'Biggest Nutrition Challenge',
};
const OB_GROUPS = [
  { title: 'Personal Info', keys: ['full_name','age','gender','gender_other','height_cm','current_weight_kg','country','city','occupation'] },
  { title: 'Goals', keys: ['primary_goal','primary_goal_other','target_weight_kg','motivation','success_definition'] },
  { title: 'Body & Health', keys: ['medical_conditions','medical_conditions_detail','injuries','injuries_detail','medications','allergies','avg_sleep_hours','stress_level','energy_level','alcohol','smoking'] },
  { title: 'Exercise Background', keys: ['activity_level','training_experience','training_types','training_types_other','days_per_week','equipment_access','equipment_access_other','exercise_likes','exercise_dislikes'] },
  { title: 'Nutrition & Recovery', keys: ['diet_type','diet_type_other','foods_avoid','meals_per_day','water_intake','caffeine','cooking_ability','recovery_habits','nutrition_challenge'] },
];

function AccessPanel({ client, reload }) {
  const isCode = client.plan_type === 'access_code';
  const [level, setLevel] = useState(client.access_level || client.subscription_tier || 'regen_method');
  const [expiry, setExpiry] = useState((client.access_expires_at || '').slice(0, 10));
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/clients/${client.id}/access`, { access_level: level, access_expires_at: expiry || '' });
      toast.success('Access updated'); reload?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  if (!isCode) return null;
  const expired = client.access_expired;
  return (
    <div className="regen-card-elevated p-5" data-testid="access-panel">
      <div className="flex items-center justify-between">
        <div className="label-mini text-[#FFB800]">Access Code Entitlement</div>
        <span className={`pill text-[10px] ${expired ? 'bg-red-500/15 text-red-400' : 'bg-[var(--c-green-soft)] text-[var(--c-green-ink)]'}`} data-testid="access-status">{expired ? 'EXPIRED' : 'ACTIVE'}</span>
      </div>
      <div className="text-xs text-muted-foreground mt-1">Code used: <span className="font-mono">{client.access_code_used || '—'}</span></div>
      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        <div>
          <Label>Access Level</Label>
          <Select value={level} onValueChange={setLevel}>
            <SelectTrigger className="bg-transparent border-[var(--c-hair)] h-10" data-testid="access-level-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="regen_method">Full YourReGen Protocol</SelectItem>
              <SelectItem value="regen_plus">YourReGen+</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Access Expires</Label>
          <Input type="date" value={expiry} onChange={e => setExpiry(e.target.value)} data-testid="access-expiry-input" className="bg-transparent border-[var(--c-hair)] h-10" />
        </div>
      </div>
      <Button onClick={save} disabled={saving} data-testid="access-save-btn" className="mt-3 bg-[#FFB800] text-[#081726] font-semibold">{saving ? 'Saving…' : 'Save Access'}</Button>
    </div>
  );
}


function OnboardingView({ data }) {
  if (!data) {
    return <div className="regen-card-elevated p-8 text-center text-muted-foreground" data-testid="onboarding-empty">This client has not completed onboarding yet.</div>;
  }
  const fmt = (v) => Array.isArray(v) ? v.join(', ') : (v === '' || v == null ? '—' : String(v));
  const photos = data.photos || {};
  return (
    <div className="space-y-4" data-testid="onboarding-view">
      {OB_GROUPS.map(g => {
        const rows = g.keys.filter(k => data[k] !== undefined && data[k] !== '' && !(Array.isArray(data[k]) && data[k].length === 0));
        if (rows.length === 0) return null;
        return (
          <div key={g.title} className="regen-card-elevated p-5">
            <div className="label-mini text-[#00C8FF] mb-3">{g.title}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
              {rows.map(k => (
                <div key={k} className="flex justify-between gap-3 border-b border-[var(--c-hair)] pb-2" data-testid={`ob-${k}`}>
                  <span className="text-sm text-muted-foreground">{OB_LABELS[k] || k}</span>
                  <span className="text-sm font-medium text-right">{fmt(data[k])}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
      {(photos.front || photos.side || photos.back) && (
        <div className="regen-card-elevated p-5">
          <div className="label-mini text-[#A78BFA] mb-3">Starting Photos (private)</div>
          <div className="grid grid-cols-3 gap-3">
            {['front','side','back'].map(a => (
              <div key={a} className="aspect-[3/4] rounded-xl overflow-hidden bg-[var(--c-film)] relative" data-testid={`ob-photo-${a}`}>
                {photos[a] ? <img src={privateFileUrl(photos[a])} alt={a} className="w-full h-full object-cover" /> : <div className="flex items-center justify-center h-full text-xs text-muted-foreground">No photo</div>}
                <span className="absolute bottom-0 inset-x-0 text-center text-[10px] font-semibold py-1 bg-black/40 capitalize">{a}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CoachNotes({ clientId, notes, reload }) {
  const [val, setVal] = useState('');
  const add = async () => {
    if (!val.trim()) return;
    await api.post(`/clients/${clientId}/coach-notes`, { note: val });
    setVal(''); toast.success('Note added'); reload();
  };
  const del = async (noteId) => {
    await api.delete(`/clients/${clientId}/coach-notes/${noteId}`);
    toast.success('Note deleted'); reload();
  };
  return (
    <div className="regen-card-elevated p-5" data-testid="coach-notes">
      <div className="flex items-center gap-2 label-mini text-[#FFB800] mb-3"><StickyNote className="w-3.5 h-3.5" /> Private Coach Notes</div>
      <div className="flex gap-2">
        <Input value={val} onChange={e => setVal(e.target.value)} placeholder="Add a private note about this client…" data-testid="coach-note-input" className="bg-regen-card border-[var(--c-hair)]" />
        <Button onClick={add} data-testid="coach-note-add" className="bg-[#FFB800] text-[#081726] font-semibold">Add</Button>
      </div>
      <div className="mt-4 space-y-2">
        {notes.length === 0 && <div className="text-sm text-muted-foreground text-center py-4">No notes yet.</div>}
        {notes.map(n => (
          <div key={n.id} className="rounded-xl bg-[var(--c-film)] p-3 flex items-start justify-between gap-3" data-testid={`coach-note-${n.id}`}>
            <div>
              <div className="text-sm">{n.note}</div>
              <div className="text-[11px] text-muted-foreground mt-1">{n.coach_name} · {format(new Date(n.created_at), 'MMM d, yyyy HH:mm')}</div>
            </div>
            <button onClick={() => del(n.id)} data-testid={`coach-note-del-${n.id}`} className="text-muted-foreground hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}


function NutritionTargetEditor({ client, onSaved }) {
  const t = client.nutrition_target || {};
  const [form, setForm] = useState({
    calories: t.calories || '', protein: t.protein || '', carbs: t.carbs || '', fat: t.fat || '',
  });
  const suggest = async () => {
    const cal = parseFloat(form.calories) || 0;
    if (cal <= 0) return toast.error('Enter calories first');
    const r = await api.get('/nutrition/suggest-macros', { params: { calories: cal, client_id: client.id } });
    setForm(f => ({ ...f, protein: r.data.protein, carbs: r.data.carbs, fat: r.data.fat }));
    toast.success('Macros auto-calculated from goal — adjust if needed');
  };
  const save = async () => {
    await api.put(`/clients/${client.id}/nutrition-target`, {
      calories: parseFloat(form.calories) || 0, protein: parseFloat(form.protein) || 0,
      carbs: parseFloat(form.carbs) || 0, fat: parseFloat(form.fat) || 0,
    });
    toast.success('Nutrition target saved (manual override)');
    onSaved?.();
  };
  const fields = [
    { k: 'protein', l: 'Protein', u: 'g' },
    { k: 'carbs', l: 'Carbs', u: 'g' }, { k: 'fat', l: 'Fat', u: 'g' },
  ];
  return (
    <div className="regen-card-elevated p-5" data-testid="nutrition-target-editor">
      <div className="flex items-center justify-between mb-1">
        <div className="label-mini text-[#FFB800]">Daily Nutrition Targets</div>
        {t.source && <span className="pill text-[10px] bg-[var(--c-film)] text-muted-foreground">{t.source === 'manual' ? 'Manual override' : 'Auto from meal plan'}</span>}
      </div>
      <p className="text-xs text-muted-foreground mb-3">Enter just the daily calorie target and Save — macros (Protein/Carbs/Fat) are auto-calculated from the client's goal. Use "Suggest Macros" to preview or edit any value before saving.</p>
      <div className="flex gap-2 items-end mb-3">
        <div className="flex-1">
          <label className="label-mini">Calories (kcal)</label>
          <Input type="number" value={form.calories} onChange={e => setForm({ ...form, calories: e.target.value })}
            data-testid="nt-calories" className="bg-regen-card border-[var(--c-hair)] h-11 font-mono mt-1" />
        </div>
        <Button onClick={suggest} data-testid="suggest-macros-btn" variant="outline" className="h-11 border-[#FFB800]/40 text-[#FFB800]">Suggest Macros</Button>
      </div>
      <div className="grid grid-cols-3 gap-2.5">
        {fields.map(f => (
          <div key={f.k}>
            <label className="label-mini">{f.l} ({f.u})</label>
            <Input type="number" value={form[f.k]} onChange={e => setForm({ ...form, [f.k]: e.target.value })}
              data-testid={`nt-${f.k}`} className="bg-regen-card border-[var(--c-hair)] h-11 font-mono mt-1" />
          </div>
        ))}
      </div>
      <Button onClick={save} data-testid="save-nutrition-target" className="mt-3 bg-[#FFB800] text-[#081726] font-semibold">Save Target</Button>
    </div>
  );
}

function DailyNutritionReview({ clientId }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/nutrition/daily-summary', { params: { client_id: clientId, date } }).then(r => setData(r.data)).catch(() => setData(null));
  }, [clientId, date]);

  const FLAG = { green: '#65A30D', orange: '#FFB800', red: '#FF5C5C', none: '#5B6B7E' };
  const macros = [
    { k: 'calories', l: 'Calories', u: '' }, { k: 'protein', l: 'Protein', u: 'g' },
    { k: 'carbs', l: 'Carbs', u: 'g' }, { k: 'fat', l: 'Fat', u: 'g' },
  ];
  const meals = data?.built_meals || [];
  const extras = data?.extras || [];

  return (
    <div className="regen-card-elevated p-5" data-testid="daily-nutrition-review">
      <div className="flex items-center justify-between mb-3">
        <div className="label-mini text-[#FFB800]">Daily Nutrition Review</div>
        <Input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="review-date" className="w-auto bg-regen-card border-[var(--c-hair)] h-9 text-xs" />
      </div>

      {!data?.has_target ? (
        <div className="text-sm text-muted-foreground py-2">Set a nutrition target above to track compliance.</div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-muted-foreground">Logged intake vs target</span>
            {data.has_logged
              ? data.compliance != null && <span className="font-mono font-bold text-sm" style={{ color: data.compliance >= 85 ? '#65A30D' : data.compliance >= 60 ? '#FFB800' : '#FF5C5C' }}>{data.compliance}% Nutrition</span>
              : <span className="text-[11px] text-muted-foreground">No food logged this day</span>}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
            {macros.map(m => {
              const fl = (data.flags || {})[m.k] || 'none';
              return (
                <div key={m.k} className="rounded-xl bg-[var(--c-film)] p-2.5" data-testid={`review-${m.k}`}>
                  <div className="label-mini">{m.l}</div>
                  <div className="font-mono mt-1 text-sm">
                    <span style={{ color: FLAG[fl] }}>{Math.round((data.logged || {})[m.k] || 0)}</span>
                    <span className="text-muted-foreground text-xs"> / {Math.round((data.target || {})[m.k] || 0)}{m.u}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <div className="label-mini mb-2">Meals Logged ({meals.length})</div>
      {meals.length === 0 && <div className="text-sm text-muted-foreground mb-3">No meals built this day.</div>}
      <div className="space-y-2 mb-4">
        {meals.map(m => {
          const tt = m.totals || {};
          return (
            <div key={m.id} className="rounded-xl bg-[var(--c-film)] p-3" data-testid={`review-meal-${m.id}`}>
              <div className="flex items-center justify-between">
                <div className="font-medium text-sm">{m.slot} · {m.name}</div>
                <div className="font-mono text-xs text-[#FFB800]">{Math.round(tt.calories || 0)} kcal · P{Math.round(tt.protein || 0)} C{Math.round(tt.carbs || 0)} F{Math.round(tt.fat || 0)}</div>
              </div>
              <div className="mt-1.5 text-[11px] text-muted-foreground">
                {(m.items || []).map((it, i) => `${it.food?.name || '—'} ${it.grams}g`).join(' · ') || '—'}
              </div>
            </div>
          );
        })}
      </div>

      <div className="label-mini mb-2">Additional Foods ({extras.length})</div>
      {extras.length === 0 ? <div className="text-sm text-muted-foreground">No extra foods logged.</div> : (
        <div className="space-y-1.5">
          {extras.map(e => (
            <div key={e.id} className="flex items-center justify-between text-sm" data-testid={`review-extra-${e.id}`}>
              <span>{e.name}{e.quantity ? ` · ${e.quantity} ${e.unit}` : e.grams ? ` · ${e.grams}g` : ''}{e.is_custom && <span className="text-[10px] text-muted-foreground ml-1">custom</span>}</span>
              <span className="font-mono text-[#FFB800]">{Math.round(e.calories)} kcal</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ClientCustomFoods({ clientId }) {
  const [foods, setFoods] = useState([]);
  const load = async () => { const r = await api.get(`/clients/${clientId}/custom-foods`); setFoods(r.data); };
  useEffect(() => { load(); }, [clientId]);
  const promote = async (f) => {
    if (!confirm(`Promote "${f.name}" to the shared YourReGen food database?`)) return;
    await api.post(`/foods/${f.id}/promote`); toast.success(`${f.name} added to YourReGen database`); load();
  };
  return (
    <div className="regen-card-elevated p-5" data-testid="client-custom-foods">
      <div className="label-mini text-[#FFB800] mb-1">Client Custom Foods</div>
      <p className="text-xs text-muted-foreground mb-3">Foods this client saved themselves. Promote frequently-used ones to the shared database.</p>
      {foods.length === 0 ? (
        <div className="text-sm text-muted-foreground">No custom foods saved yet.</div>
      ) : (
        <div className="space-y-2">
          {foods.map(f => (
            <div key={f.id} className="flex items-center gap-3 rounded-xl bg-[var(--c-film)] p-2.5" data-testid={`custom-food-${f.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{f.name}</div>
                <div className="text-[11px] font-mono text-muted-foreground">{Math.round(f.calories_per_100g)} kcal · P{Math.round(f.protein_per_100g)} C{Math.round(f.carbs_per_100g)} F{Math.round(f.fat_per_100g)} <span className="opacity-60">/ serving</span></div>
              </div>
              <Button size="sm" variant="outline" onClick={() => promote(f)} data-testid={`promote-food-${f.id}`} className="border-[var(--c-green-soft)] text-[var(--c-green-ink)] h-8 text-xs">Promote</Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
